import { List } from 'immutable';
import isEmpty from 'transmute/isEmpty';
import LogEventMessage, { LogEventMessageKeys } from 'automation-ui-log-events/records/LogEventMessage/LogEventMessage';
import LogEventMessageFieldDefinition, { LogEventMessageFieldDefinitionKeys } from 'automation-ui-log-events/records/LogEventMessage/LogEventMessageFieldDefinition';
import TranslatedLogEventMessageFieldDefinition, { TranslatedLogEventMessageFieldDefinitionKeys } from 'automation-ui-log-events/records/LogEventMessage/TranslatedLogEventMessageFieldDefinition';
import LogEventMessageConfig from 'automation-ui-log-events/records/LogEventMessage/LogEventMessageConfig';
import TranslatedLogEventMessageConfig from 'automation-ui-log-events/records/LogEventMessage/TranslatedLogEventMessageConfig';
import { FieldKeys, MissingPropertyErrorTypes } from 'automation-ui-log-events/constants/LogEventMessageConstants';
import * as defaultMessageUtils from 'automation-ui-log-events/records/LogEventMessage/defaultMessageUtils';
import * as subtypedMessageUtils from 'automation-ui-log-events/records/LogEventMessage/subtypedMessageUtils';
import * as preenrollmentMessageUtils from 'automation-ui-log-events/records/LogEventMessage/preenrollmentMessageUtils';
import * as translatedMessageUtils from 'automation-ui-log-events/records/LogEventMessage/translatedMessageUtils';
import { LogEventTypes } from 'automation-ui-log-events/constants/LogEventConstants';
import { CREATE_OBJECT } from 'automation-ui-action-features/constants/ActionTypes';
import { getType, getAction, getErrorSubType, getFailSubType, getErrorType } from 'automation-ui-log-events/records/LogEvent/utils';
import { getHistoryMessage } from 'automation-ui-log-events/records/LogEvent/contextMapUtils';
import LogEventKeys from 'automation-ui-log-events/constants/LogEventKeys';
/**
 * Configs
 */

const DEFAULT = new LogEventMessageConfig().populate(new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DEFAULT,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: defaultMessageUtils.getLogEventMessage,
  [LogEventMessageFieldDefinitionKeys.IS_PRIMARY_RISK_MESSAGE_FIELD]: true
}));
const SUBTYPED = new LogEventMessageConfig().populate(new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DEFAULT,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: defaultMessageUtils.getLogEventMessage,
  [LogEventMessageFieldDefinitionKeys.IS_SECONDARY_RISK_MESSAGE_FIELD]: true
}), new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DETAILS,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: subtypedMessageUtils.getLogEventMessage,
  [LogEventMessageFieldDefinitionKeys.IS_PRIMARY_RISK_MESSAGE_FIELD]: true
}), new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.FOR_MORE,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: subtypedMessageUtils.getSupplementalLogEventMessage('informational')
}));
const PREENROLLMENT = new LogEventMessageConfig().populate(new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.ENROLLMENT_ATTEMPT,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: preenrollmentMessageUtils.getLogEventPrimaryMessage,
  [LogEventMessageFieldDefinitionKeys.IS_SECONDARY_RISK_MESSAGE_FIELD]: true
}), new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.ENROLLMENT_RESULT,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: preenrollmentMessageUtils.getLogEventSecondaryMessage,
  [LogEventMessageFieldDefinitionKeys.IS_PRIMARY_RISK_MESSAGE_FIELD]: true
}));

// Used for specific errors within `MissingPropertyErrorTypes`
const PROPERTY_DETAILS = new LogEventMessageConfig().populate(new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DEFAULT,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: defaultMessageUtils.getLogEventMessage,
  [LogEventMessageFieldDefinitionKeys.IS_PRIMARY_RISK_MESSAGE_FIELD]: true
}), new LogEventMessageFieldDefinition({
  [LogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DETAILED_PROPERTIES,
  [LogEventMessageFieldDefinitionKeys.PATH_KEYS_CONSTRUCTOR]: defaultMessageUtils.getDetailedPropertiesLogEventMessage
}));
const TRANSLATED_MESSAGE = new TranslatedLogEventMessageConfig().populate(new TranslatedLogEventMessageFieldDefinition({
  [TranslatedLogEventMessageFieldDefinitionKeys.FIELD_KEY]: FieldKeys.DEFAULT,
  [TranslatedLogEventMessageFieldDefinitionKeys.MESSAGE_RESOLVER]: translatedMessageUtils.getLogEventMessage,
  [LogEventMessageFieldDefinitionKeys.IS_PRIMARY_RISK_MESSAGE_FIELD]: true
}));

/**
 * Helper utils
 */

const getFallbackLogEventMessage = logEvent => {
  const type = getType(logEvent);
  return new LogEventMessage({
    [LogEventMessageKeys.FIELD_KEY]: FieldKeys.DEFAULT,
    [LogEventMessageKeys.PATH]: `${LogEventKeys.ROOT_KEY}.${type}.default`,
    [LogEventMessageKeys.IS_JSX_MESSAGE]: false,
    [LogEventMessageKeys.IS_PRIMARY_RISK_MESSAGE]: true
  });
};
export const getLogEventMessages = (logEvent, objectTypeId) => {
  const type = getType(logEvent);
  const errorSubType = getErrorSubType(logEvent);
  const failSubType = getFailSubType(logEvent);
  const extHistoryMessage = getHistoryMessage(logEvent);
  const errorType = getErrorType(logEvent);
  const actionObj = getAction(logEvent);
  let config = DEFAULT;
  if (type === LogEventTypes.PRE_ENROLLMENT) {
    config = PREENROLLMENT;
  } else if (extHistoryMessage) {
    config = TRANSLATED_MESSAGE;
  } else if (failSubType || errorSubType) {
    config = SUBTYPED;
  } else if (errorType && MissingPropertyErrorTypes.includes(errorType) && actionObj && actionObj.actionType === CREATE_OBJECT) {
    config = PROPERTY_DETAILS;
  }
  let visibleLogEventMessages = config.getVisibleLogEventMessages(logEvent, objectTypeId);
  if (isEmpty(visibleLogEventMessages)) {
    if (config !== DEFAULT) {
      visibleLogEventMessages = DEFAULT.getVisibleLogEventMessages(logEvent, objectTypeId);
      if (isEmpty(visibleLogEventMessages)) {
        return List([getFallbackLogEventMessage(logEvent)]);
      }
    } else {
      return List([getFallbackLogEventMessage(logEvent)]);
    }
  }
  return visibleLogEventMessages;
};